import { Property } from "@/models/properties.model";
import { store } from "@/lib/store";
import { ReadAllResponse, uuid } from "@/interfaces";
import { requirePermission } from "@/lib/permissions";
import { api } from "@/lib/api";
import { contentTypeOf, putPhoto } from "@/lib/appointment-photos";
import { everyProperty, toCreateRequest, toDetailsRequest, toProperty, toPropertyWithRelations, type PropertyWithRelations } from "@/repositories/property-records";

export type { PropertyWithRelations };

export type ServicedProperty = { id: uuid; name: string; address: string; customer: uuid | null };

export type PropertyFilters = {
  type?: Property["type"][number];
  structure?: Property["structure"][number];
  class?: Property["class"][number];
  sortBy?: keyof Property;
  page?: number;
  pageSize?: number;
  sortDirection?: "asc" | "desc";
};

const actingOrganization = () => store.getState().organization.activeOrgId ?? undefined;

export const PropertyRepository = {
  async getAll({
    type,
    structure,
    class: _class,
    sortBy = "created_at",
    page = 1,
    pageSize = 10,
    sortDirection = "desc",
  }: PropertyFilters): Promise<ReadAllResponse<Property>> {
    await requirePermission("properties.view");

    const customer = actingOrganization();
    if (!customer) return { data: [], count: 0 };

    const { items, total } = await api.properties.list({
      actingOrganization: customer,
      customer,
      ...(type ? { type } : {}),
      ...(structure ? { structure } : {}),
      ...(_class ? { class: _class } : {}),
      sort: sortBy === "name" ? "name" : "createdAt",
      order: sortDirection,
      page,
      pageSize,
    });

    return { data: items.map(toProperty), count: total };
  },

  async delete(id: uuid): Promise<void> {
    await requirePermission("properties.delete");
    await api.properties.delete(id);
  },

  async find(id: uuid): Promise<PropertyWithRelations> {
    await requirePermission("properties.view");

    return toPropertyWithRelations(await api.properties.get(id, { actingOrganization: actingOrganization() }));
  },

  async servicedBy(vendorOrgId: uuid): Promise<ServicedProperty[]> {
    const properties = await everyProperty(api.properties.list, { actingOrganization: vendorOrgId, vendor: vendorOrgId, sort: "name", order: "asc" });
    return properties.map(({ id, name, address, customer }) => ({ id, name, address: address ?? "", customer: customer?.id ?? null }));
  },

  async updateDetails(id: uuid, payload: Partial<Property>): Promise<Property> {
    await requirePermission("properties.update");
    return toProperty(await api.properties.setDetails(id, toDetailsRequest(payload)));
  },

  async create(payload: Partial<Property>): Promise<Property> {
    await requirePermission("properties.create");
    const customer = actingOrganization();
    if (!customer) throw new Error("Choose the customer to add the property to.");
    return toProperty(await api.properties.create(toCreateRequest(customer, payload)));
  },

  async setImage(propertyId: uuid, file: File): Promise<Property> {
    await requirePermission("properties.upload_images");
    const contentType = contentTypeOf(file);
    const upload = await api.properties.imageUpload(propertyId, { contentType });
    await putPhoto(upload, file, contentType);
    return toProperty(await api.properties.setImage(propertyId, { imageUrl: upload.photoUrl }));
  },

  async geocodeAddress(address: string): Promise<any> {
    const data = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${address}.json?proximity=ip&access_token=${
        import.meta.env.VITE_MAPBOX_KEY
      }`
    );
    return await data.json();
  },
};
